import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, TextField, InputAdornment, IconButton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import { useState } from 'react';
import { heritageColors } from '../../../heritageTheme.ts';
import { RealWidgetPreview } from '../../../dummies/WidgetPreview';

const MyWidgets = () => {
    const navigate = useNavigate();
    const { organizationId } = useParams();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('my-widgets');

    const allDashboards = useSelector(state => state.screens?.dashboards || []);
    const organization = useSelector(state => state.organization);
    const catalog = useSelector(state => state.app?.catalog || []);

    const myWidgets = allDashboards.flatMap(dashboard =>
        (dashboard.current?.widgets || []).map(widget => ({
            ...widget,
            dashboardId: dashboard.dashboardId,
            dashboardName: dashboard.name
        }))
    );

    const savedWidgets = organization?.savedWidgets || [];

    const filteredMyWidgets = myWidgets.filter(widget => {
        const catalogWidget = catalog.find(c => c.componentKey === widget.componentKey);
        const widgetName = catalogWidget?.name || widget.componentKey;

        return widgetName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            widget.dashboardName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            widget.props?.title?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const filteredSavedWidgets = savedWidgets.filter(widget => {
        const catalogWidget = catalog.find(c => c.componentKey === widget.componentKey);
        const widgetName = catalogWidget?.name || widget.componentKey;

        return widgetName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            widget.props?.title?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const handleBack = () => {
        navigate(`/${organizationId}/screens`);
    };

    return (
        <Box sx={{ width: '100%', height: '100vh', bgcolor: heritageColors.white, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.04) 0%, rgba(255,255,255,0) 70%)', bgcolor: heritageColors.white, px: 4, py: 3, boxShadow: 'none', flexShrink: 0, borderBottom: '1px solid rgba(30,58,95,0.09)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            color: heritageColors.navy,
                            '&:hover': {
                                bgcolor: heritageColors.creamLight
                            }
                        }}
                    >
                        <ArrowForwardIcon />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{ color: heritageColors.navy, fontWeight: 700, mb: 0.5 }}>הרכיבים שלי</Typography>
                        <Typography variant="body2" sx={{ color: heritageColors.slate, opacity: 0.8 }}>
                            {activeTab === 'my-widgets' ? `${myWidgets.length} רכיבים מ-${allDashboards.length} לוחות` : `${savedWidgets.length} רכיבים שמורים`}
                        </Typography>
                    </Box>
                </Box>
                <TextField fullWidth placeholder={activeTab === 'my-widgets' ? "חפש רכיב או לוח..." : "חפש ברכיבים שמורים..."} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: heritageColors.slate }} /></InputAdornment>) }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: heritageColors.creamLight, color: heritageColors.navy, '&:hover fieldset': { borderColor: heritageColors.gold }, '& fieldset': { borderColor: heritageColors.border }, '&.Mui-focused fieldset': { borderColor: heritageColors.gold } }, '& .MuiInputBase-input::placeholder': { color: heritageColors.slate, opacity: 0.6 } }} />
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, borderBottom: `2px solid ${heritageColors.border}` }}>
                    <Button
                        onClick={() => { setActiveTab('my-widgets'); setSearchQuery(''); }}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: '8px 8px 0 0',
                            bgcolor: activeTab === 'my-widgets' ? heritageColors.navy : 'transparent',
                            color: activeTab === 'my-widgets' ? heritageColors.white : heritageColors.slate,
                            fontWeight: 600,
                            fontSize: '1rem',
                            textTransform: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'my-widgets' ? 'none' : `2px solid transparent`,
                            boxShadow: 'none',
                            position: 'relative',
                            bottom: '-2px',
                            '&:hover': {
                                bgcolor: activeTab === 'my-widgets' ? heritageColors.navyDark : heritageColors.creamLight,
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        רכיבים בשימוש ({myWidgets.length})
                    </Button>
                    <Button
                        onClick={() => { setActiveTab('saved'); setSearchQuery(''); }}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: '8px 8px 0 0',
                            bgcolor: activeTab === 'saved' ? heritageColors.navy : 'transparent',
                            color: activeTab === 'saved' ? heritageColors.white : heritageColors.slate,
                            fontWeight: 600,
                            fontSize: '1rem',
                            textTransform: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'saved' ? 'none' : `2px solid transparent`,
                            boxShadow: 'none',
                            position: 'relative',
                            bottom: '-2px',
                            '&:hover': {
                                bgcolor: activeTab === 'saved' ? heritageColors.navyDark : heritageColors.creamLight,
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        רכיבים שמורים ({savedWidgets.length})
                    </Button>
                </Box>
                {activeTab === 'my-widgets' ? (
                    filteredMyWidgets.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}><Typography variant="h6" sx={{ color: heritageColors.slate, mb: 1 }}>לא נמצאו רכיבים</Typography><Typography variant="body2" sx={{ color: heritageColors.slate, opacity: 0.7 }}>{searchQuery ? 'נסה לשנות את החיפוש' : 'טרם הוספת רכיבים ללוחות'}</Typography></Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 3, rowGap: 7 }}>
                            {filteredMyWidgets.map((widget, index) => {
                                const catalogWidget = catalog.find(c => c.componentKey === widget.componentKey);
                                const widgetName = catalogWidget?.name || widget.componentKey;

                                return (
                                    <Box key={`${widget.widgetId}-${index}`} sx={{ cursor: 'pointer', position: 'relative', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ borderRadius: '16px', overflow: 'hidden', position: 'relative', mb: 0.75, border: `1px solid ${heritageColors.border}`, transition: 'all 0.2s ease', '&:hover': { borderColor: heritageColors.navy, boxShadow: '0 10px 28px rgba(30, 58, 95, 0.16), 0 2px 6px rgba(30, 58, 95, 0.08)' } }}>
                                            <Box sx={{ width: '100%', paddingTop: '75%', position: 'relative', bgcolor: heritageColors.bgLight }}>
                                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                    <RealWidgetPreview widget={widget} catalog={catalog} scale={1} />
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box sx={{ p: 1.5, background: 'linear-gradient(135deg, #152a47 0%, #1e3a5f 100%)', borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: '70px', justifyContent: 'center' }}>
                                            <Typography sx={{ fontWeight: 700, color: heritageColors.white, mb: 0.5, fontSize: '0.9rem', textAlign: 'right', lineHeight: 1.3 }}>
                                                {widgetName}
                                            </Typography>
                                            <Typography sx={{ color: heritageColors.creamLight, fontSize: '0.7rem', lineHeight: 1.3, textAlign: 'right', opacity: 0.9 }}>
                                                {widget.dashboardName}
                                            </Typography>
                                            {widget.props?.title && (
                                                <Typography sx={{ color: heritageColors.gold, fontSize: '0.7rem', lineHeight: 1.3, textAlign: 'right', mt: 0.5, fontWeight: 500 }}>
                                                    {widget.props.title}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )
                ) : (
                    filteredSavedWidgets.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}><Typography variant="h6" sx={{ color: heritageColors.slate, mb: 1 }}>אין רכיבים שמורים</Typography><Typography variant="body2" sx={{ color: heritageColors.slate, opacity: 0.7 }}>שמור רכיבים מהלוחות שלך לשימוש חוזר</Typography></Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 3, rowGap: 7 }}>
                            {filteredSavedWidgets.map((widget, index) => {
                                const catalogWidget = catalog.find(c => c.componentKey === widget.componentKey);
                                const widgetName = catalogWidget?.name || widget.componentKey;

                                return (
                                    <Box key={`${widget.widgetId}-${index}`} sx={{ cursor: 'pointer', position: 'relative', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ borderRadius: '16px', overflow: 'hidden', position: 'relative', mb: 0.75, border: `1px solid ${heritageColors.border}`, transition: 'all 0.2s ease', '&:hover': { borderColor: heritageColors.navy, boxShadow: '0 10px 28px rgba(30, 58, 95, 0.16), 0 2px 6px rgba(30, 58, 95, 0.08)' } }}>
                                            <Box sx={{ width: '100%', paddingTop: '75%', position: 'relative', bgcolor: heritageColors.bgLight }}>
                                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                    <RealWidgetPreview widget={widget} catalog={catalog} scale={1} />
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box sx={{ p: 1.5, background: 'linear-gradient(135deg, #152a47 0%, #1e3a5f 100%)', borderRadius: '12px', display: 'flex', flexDirection: 'column', minHeight: '70px', justifyContent: 'center' }}>
                                            <Typography sx={{ fontWeight: 700, color: heritageColors.white, mb: 0.5, fontSize: '0.9rem', textAlign: 'right', lineHeight: 1.3 }}>
                                                {widgetName}
                                            </Typography>
                                            {widget.props?.title && (
                                                <Typography sx={{ color: heritageColors.gold, fontSize: '0.7rem', lineHeight: 1.3, textAlign: 'right', fontWeight: 500 }}>
                                                    {widget.props.title}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};

export default MyWidgets;
